import type { Customer } from '@/api/customers-types'
import { queryClient } from '@/providers/query-client'
import { useQueryClient } from '@tanstack/react-query'
import { StoredBooleanSubject, StoredObjectSubject, StoredStringSubject } from './storage'

/** Persisted auth token for the current customer. Empty string = logged out. */
export const customerToken = new StoredStringSubject('customer-token', '')

/** Persisted customer object for synchronous access after login. */
export const customerSubject = new StoredObjectSubject<Customer | null>('customer', null)

/** Whether the user has accepted the current terms (checked against termsLastUpdatedAt). */
export const termsAccepted = new StoredBooleanSubject('terms-accepted', false)

export type GetAuthResponse = { isLoggedIn: false } | { isLoggedIn: true; token: string; customer: Customer }

export const getAuth = (): GetAuthResponse => {
	const token = customerToken.value
	const customer = customerSubject.value
	if (!token || !customer) {
		return { isLoggedIn: false }
	}
	return { isLoggedIn: true, token, customer }
}

export const clearAuth = () => {
	customerToken.next('')
	customerSubject.next(null)
	termsAccepted.next(false)
}

export const logIn = (token: string, customer: Customer) => {
	customerToken.next(token)
	customerSubject.next(customer)
}

export const logOut = () => {
	clearAuth()
	queryClient.clear()
}

/** @deprecated Use logOut from auth-provider or utils/auth instead */
export function useLogout() {
	const queryClient = useQueryClient()

	return () => {
		queryClient?.clear()
		clearAuth()
	}
}
